import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../../../../config';
import { toApiError } from '../../../../lib/api-error';

/** A flow rendered as a node in the lineage graph (mirrors the API response). */
export interface LineageNode {
  id: string;
  name: string;
  flowType: string;
  status: string;
  direction: 'self' | 'upstream' | 'downstream';
}

/** A dependency link: `from` depends on `to`, carried by seed node `nodeId`. */
export interface LineageEdge {
  from: string;
  to: string;
  onFailure: 'block' | 'warn';
  snapshotVersion: number | null;
  nodeId: string;
}

export interface FlowLineage {
  nodes: LineageNode[];
  edges: LineageEdge[];
}

/**
 * Find the flow's leading root node — the first node (in load order) that has
 * no incoming edge — so a newly added seed can be wired in ahead of it. Edges
 * may use either the API shape (`targetNodeId`) or the canvas shape (`target`).
 * `excludeNodeId` keeps the just-created seed itself out of the running, since
 * it also has no incoming edge yet. Returns null for an empty flow (the seed is
 * then the lone root and needs no edge).
 */
export function findLeadingRootNodeId(
  nodes: Array<{ id: string }>,
  edges: Array<{ targetNodeId?: string; target?: string }>,
  excludeNodeId: string
): string | null {
  const nodesWithIncoming = new Set(
    edges.map((e) => e.targetNodeId ?? e.target).filter(Boolean) as string[]
  );
  const root = nodes.find((n) => n.id !== excludeNodeId && !nodesWithIncoming.has(n.id));
  return root?.id ?? null;
}

/**
 * Fetch the bidirectional seed dependency lineage for a flow: the flows that
 * (transitively) depend on it and the seeds it (transitively) depends on.
 */
export function useFlowLineage(workflowId: string | null, enabled = true) {
  return useQuery<FlowLineage>({
    queryKey: ['flow-lineage', workflowId],
    enabled: enabled && !!workflowId,
    staleTime: 15_000,
    queryFn: async () => {
      const res = await fetch(`${API_URL}/workflows/${workflowId}/lineage`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to load flow lineage');
      const data = await res.json();
      return data.lineage as FlowLineage;
    },
  });
}

/**
 * Prune a dependency link: delete the seed node (`nodeId`) from the dependent
 * flow (`dependentWorkflowId`). Reuses the existing node CRUD endpoint.
 */
export function usePruneDependency(focusWorkflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { dependentWorkflowId: string; nodeId: string }) => {
      const res = await fetch(
        `${API_URL}/workflows/${params.dependentWorkflowId}/nodes/${params.nodeId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) throw await toApiError(res, 'Failed to remove dependency');
      return res.json();
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['flow-lineage', focusWorkflowId] });
      queryClient.invalidateQueries({
        queryKey: ['workflow-detail', params.dependentWorkflowId],
      });
    },
  });
}

/**
 * Add a dependent: insert a `seed` node into `dependentWorkflowId` that points at
 * the focus seed flow, then wire that seed in as the dependent flow's leading
 * step (an edge from the new seed to the flow's current root) so it renders as a
 * connected node rather than a disconnected orphan. Reuses the existing
 * node-create + edge-create endpoints. Cycle creation is prevented up-front by
 * excluding downstream flows from the picker, with the execute-time seed-DAG
 * gate as the ultimate guard.
 */
export function useAddDependency(focusWorkflowId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { dependentWorkflowId: string; seedWorkflowName: string }) => {
      if (!focusWorkflowId) throw new Error('No focus workflow');
      const { dependentWorkflowId } = params;

      // 1. Create the seed node in the dependent flow.
      const res = await fetch(`${API_URL}/workflows/${dependentWorkflowId}/nodes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          nodeType: 'seed',
          configJson: {
            seedWorkflowId: focusWorkflowId,
            seedWorkflowName: params.seedWorkflowName,
            snapshotVersion: null,
            onFailure: 'block',
          },
        }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to add dependency');
      const created = await res.json();
      const newSeedNodeId: string | undefined = created?.node?.id;

      // 2. Wire the seed in as the leading step: connect it to the dependent
      // flow's current root (the node with no incoming edge), mirroring how
      // prepend works in the flow designer. Best-effort — the dependency is
      // already persisted as a node, so an edge failure leaves the seed as an
      // orphan (the prior behavior) rather than losing the link.
      if (newSeedNodeId) {
        const detailRes = await fetch(`${API_URL}/workflows/${dependentWorkflowId}`, {
          credentials: 'include',
        });
        if (detailRes.ok) {
          const detail = await detailRes.json();
          const wf = detail?.workflow ?? detail;
          const nodes: Array<{ id: string }> = wf?.nodes ?? [];
          const edges: Array<{ targetNodeId?: string; target?: string }> = wf?.edges ?? [];
          const rootId = findLeadingRootNodeId(nodes, edges, newSeedNodeId);
          if (rootId) {
            const edgeRes = await fetch(`${API_URL}/workflows/${dependentWorkflowId}/edges`, {
              method: 'POST',
              credentials: 'include',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                sourceNodeId: newSeedNodeId,
                targetNodeId: rootId,
                edgeType: 'default',
              }),
            });
            if (!edgeRes.ok) throw await toApiError(edgeRes, 'Failed to wire seed into flow');
          }
        }
      }

      return created;
    },
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({ queryKey: ['flow-lineage', focusWorkflowId] });
      queryClient.invalidateQueries({
        queryKey: ['workflow-detail', params.dependentWorkflowId],
      });
    },
  });
}
